import { useState, useEffect, useRef } from "react";

const programs = [
  {
    id: "stress",
    label: "Stress Resilience",
    icon: "ri-mental-health-line",
    color: "#00a882",
    desc: "Personalized Apollo wearable protocols designed to reduce workplace stress, lower cortisol, and build long-term nervous system resilience — measurable from day one.",
    benefit: "19% avg. cortisol reduction",
  },
  {
    id: "focus",
    label: "Peak Performance",
    icon: "ri-brain-line",
    color: "#6366f1",
    desc: "Targeted vibration frequencies entrain brainwave activity toward deep focus states, helping sustain attention during high-demand work periods without stimulants.",
    benefit: "28% improvement in sustained focus",
  },
  {
    id: "sleep",
    label: "Recovery & Sleep",
    icon: "ri-moon-line",
    color: "#00d4a8",
    desc: "Guided sleep protocols help recover faster, reduce burnout risk, and arrive at work fully restored — directly impacting productivity and retention.",
    benefit: "37-min avg. sleep onset improvement",
  },
  {
    id: "energy",
    label: "Energy Management",
    icon: "ri-flashlight-line",
    color: "#4f46e5",
    desc: "Morning activation modes prime the nervous system for high-output days. Afternoon recovery modes prevent the 2pm slump — keeping energy sustained across full workdays.",
    benefit: "40% increase in reported alertness",
  },
];

const outcomes = [
  { icon: "ri-arrow-down-line", value: "32%", label: "Reduction in Sick Days" },
  { icon: "ri-arrow-up-line", value: "41%", label: "Increase in Productivity" },
  { icon: "ri-heart-pulse-line", value: "2,400+", label: "Employees Enrolled" },
  { icon: "ri-trophy-line", value: "94%", label: "Program Satisfaction" },
];

const testimonials = [
  {
    name: "Jennifer Marsh",
    role: "Chief People Officer, Apex Corp",
    quote: "We rolled out the Apollo Business Wellness Program to 400 employees. Sick day usage dropped 29% in the first quarter alone. The ROI was undeniable.",
    avatar: "https://public.readdy.ai/ai/img_res/03b01cdb60364df1d803db55230f3e3a.jpg",
  },
  {
    name: "David Okafor",
    role: "VP of Operations, NovaTech",
    quote: "Our teams work in high-pressure environments. Apollo's stress resilience protocols have been a game changer — focus is up, burnout is down.",
    avatar: "https://readdy.ai/api/search-image?query=professional%20male%20executive%20headshot%2C%20confident%20expression%2C%20modern%20office%20background%2C%20clean%20corporate%20portrait%20photography&width=80&height=80&seq=biz_avatar002&orientation=squarish",
  },
  {
    name: "Dr. Lisa Tran",
    role: "Corporate Health Director, MedGroup",
    quote: "The biometric reporting dashboard gives us real data on employee wellbeing. We can actually see the nervous system improvements month over month.",
    avatar: "https://readdy.ai/api/search-image?query=professional%20female%20doctor%20executive%20headshot%2C%20confident%20expression%2C%20medical%20corporate%20office%20background%2C%20clean%20portrait%20photography&width=80&height=80&seq=biz_avatar003&orientation=squarish",
  },
];

const howItWorks = [
  {
    step: "01",
    title: "Assessment & Onboarding",
    desc: "Our certified wellness coaches assess your team's needs and customize the Apollo protocol for your organization's goals.",
  },
  {
    step: "02",
    title: "Device Distribution",
    desc: "Every enrolled employee receives their Apollo Neuro wearable, pre-configured with their personalized wellness program.",
  },
  {
    step: "03",
    title: "Continuous Optimization",
    desc: "We track biometric outcomes and adjust protocols based on real data, ensuring maximum impact and ROI.",
  },
];

export default function ApolloNeuroPage() {
  const [activeProgram, setActiveProgram] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const current = programs[activeProgram];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    const particles: { x: number; y: number; vx: number; vy: number; r: number; alpha: number }[] = [];
    for (let i = 0; i < 60; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        r: Math.random() * 2 + 0.5,
        alpha: Math.random() * 0.5 + 0.3,
      });
    }

    let animId: number;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0, 168, 130, ${p.alpha})`;
        ctx.fill();
      });

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 100) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(0, 168, 130, ${0.1 * (1 - dist / 100)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(animId);
  }, []);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#f4faf7]">
      {/* Navbar */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? "bg-white/95 backdrop-blur-md shadow-sm" : "bg-transparent"}`}>
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-center justify-between h-16 md:h-20">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#00a882] to-[#00d4a8] flex items-center justify-center">
                <i className="ri-pulse-line text-white text-sm"></i>
              </div>
              <span className={`font-bold text-lg tracking-tight ${isScrolled ? "text-[#0a2a1f]" : "text-[#0a2a1f]"}`}>
                Apollo Neuro
              </span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection("science")} className="text-[#0a2a1f]/70 hover:text-[#00a882] text-sm font-medium transition-colors cursor-pointer">
                Science
              </button>
              <button onClick={() => scrollToSection("programs")} className="text-[#0a2a1f]/70 hover:text-[#00a882] text-sm font-medium transition-colors cursor-pointer">
                Programs
              </button>
              <button onClick={() => scrollToSection("how-it-works")} className="text-[#0a2a1f]/70 hover:text-[#00a882] text-sm font-medium transition-colors cursor-pointer">
                How It Works
              </button>
              <button onClick={() => scrollToSection("testimonials")} className="text-[#0a2a1f]/70 hover:text-[#00a882] text-sm font-medium transition-colors cursor-pointer">
                Testimonials
              </button>
            </div>
            <button onClick={() => scrollToSection("contact")} className="whitespace-nowrap px-5 py-2.5 rounded-full bg-[#00a882] text-white text-sm font-semibold hover:bg-[#008f6e] transition-colors cursor-pointer">
              Get a Quote
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=futuristic%20wellness%20technology%20visualization%2C%20human%20silhouette%20with%20glowing%20neural%20network%20pathways%2C%20calming%20teal%20and%20emerald%20green%20energy%20waves%2C%20biotech%20wearable%20device%20concept%2C%20clean%20modern%20aesthetic%20with%20soft%20gradients%2C%20meditation%20and%20stress%20relief%20visualization%2C%20professional%20healthcare%20technology&width=1920&height=1080&seq=apollo_hero_01&orientation=landscape"
            alt="Apollo Neuro Technology"
            className="w-full h-full object-cover object-top opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#f4faf7] via-[#f4faf7]/80 to-transparent"></div>
          <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none opacity-60" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00a882]/10 border border-[#00a882]/20 mb-6">
                <i className="ri-flashlight-line text-[#00a882] text-sm"></i>
                <span className="text-[#00a882] text-xs font-semibold tracking-wide uppercase">Clinically Validated</span>
              </div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-[#0a2a1f] leading-tight mb-6">
                Transform Your
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#00a882] to-[#00d4a8]">
                  Workforce Wellness
                </span>
              </h1>
              <p className="text-[#0a2a1f]/70 text-lg md:text-xl leading-relaxed mb-8 max-w-lg">
                Apollo Neuro's breakthrough wearable technology delivers measurable stress relief, improved focus, and better sleep — all without requiring any training or dedicated time from your employees.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button onClick={() => scrollToSection("contact")} className="whitespace-nowrap px-8 py-4 rounded-full bg-[#00a882] text-white font-semibold text-base hover:bg-[#008f6e] transition-all duration-200 cursor-pointer shadow-lg shadow-[#00a882]/20">
                  Get Enterprise Quote
                </button>
                <button onClick={() => scrollToSection("science")} className="whitespace-nowrap px-8 py-4 rounded-full border-2 border-[#0a2a1f]/20 text-[#0a2a1f] font-semibold text-base hover:border-[#00a882] hover:text-[#00a882] transition-all duration-200 cursor-pointer">
                  See the Science
                </button>
              </div>
              <div className="flex items-center gap-6 mt-10">
                <div className="flex -space-x-3">
                  {["https://readdy.ai/api/search-image?query=professional%20headshot%20diverse%20person%201&width=40&height=40&seq=team_01&orientation=squarish", "https://readdy.ai/api/search-image?query=professional%20headshot%20diverse%20person%202&width=40&height=40&seq=team_02&orientation=squarish", "https://readdy.ai/api/search-image?query=professional%20headshot%20diverse%20person%203&width=40&height=40&seq=team_03&orientation=squarish"].map((src, i) => (
                    <div key={i} className="w-10 h-10 rounded-full border-2 border-white overflow-hidden">
                      <img src={src} alt="" className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
                <div className="text-sm">
                  <span className="text-[#00a882] font-bold">2,400+</span>
                  <span className="text-[#0a2a1f]/60 ml-1">employees enrolled</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="relative w-full aspect-square max-w-md mx-auto">
                <img
                  src="https://readdy.ai/api/search-image?query=Apollo%20Neuro%20wearable%20wellness%20device%20on%20wrist%2C%20sleek%20modern%20wearable%20technology%2C%20calming%20teal%20and%20white%20tones%2C%20professional%20product%20photography%2C%20soft%20studio%20lighting%2C%20minimalist%20background%2C%20health%20and%20wellness%20technology&width=500&height=500&seq=apollo_device_01&orientation=squarish"
                  alt="Apollo Neuro Device"
                  className="w-full h-full object-cover rounded-3xl shadow-2xl"
                />
                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-xl border border-[#00a882]/10">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[#00a882]/10 flex items-center justify-center">
                      <i className="ri-heart-pulse-line text-[#00a882] text-xl"></i>
                    </div>
                    <div>
                      <div className="text-[#0a2a1f] font-bold text-lg">HRV +23%</div>
                      <div className="text-[#0a2a1f]/60 text-xs">Avg. improvement</div>
                    </div>
                  </div>
                </div>
                <div className="absolute -top-4 -right-4 bg-white rounded-2xl p-4 shadow-xl border border-[#00a882]/10">
                  <div className="flex items-center gap-2">
                    <i className="ri-star-fill text-amber-400"></i>
                    <span className="text-[#0a2a1f] font-bold">4.9/5</span>
                  </div>
                  <div className="text-[#0a2a1f]/60 text-xs mt-1">User rating</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#0a2a1f] py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {outcomes.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-[#00d4a8] font-black text-4xl md:text-5xl mb-2">{stat.value}</div>
                <div className="text-white/70 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Science Section */}
      <section id="science" className="py-24 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-16">
            <span className="text-[#00a882] text-sm font-semibold tracking-widest uppercase">The Science</span>
            <h2 className="text-4xl md:text-5xl font-black text-[#0a2a1f] mt-4 mb-6">
              Results as Powerful as Meditation
            </h2>
            <p className="text-[#0a2a1f]/70 text-lg max-w-2xl mx-auto">
              In a double-blind, placebo-controlled trial at the University of Pittsburgh, Apollo Neuro demonstrated nervous system effects comparable to experienced meditation — without requiring any practice.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="rounded-3xl overflow-hidden border border-[#00a882]/20 shadow-xl">
              <img
                src="https://readdy.ai/api/search-image?query=modern%20clinical%20research%20laboratory%2C%20scientists%20in%20white%20coats%20conducting%20biometric%20testing%2C%20advanced%20medical%20equipment%2C%20clean%20bright%20professional%20environment%2C%20teal%20and%20white%20color%20scheme%2C%20scientific%20research%20facility&width=800&height=500&seq=apollo_science_01&orientation=landscape"
                alt="Clinical Research"
                className="w-full h-80 object-cover"
              />
            </div>
            <div className="space-y-6">
              {[
                { icon: "ri-heart-pulse-line", title: "HRV Improvement", desc: "Heart rate variability improvements equivalent to 20 minutes of meditation" },
                { icon: "ri-test-tube-line", title: "Rigorous Testing", desc: "Double-blind, placebo-controlled trials with over 2,400 participants" },
                { icon: "ri-time-line", title: "Zero Time Investment", desc: "Works passively in the background while employees stay fully productive" },
                { icon: "ri-shield-check-line", title: "Clinically Validated", desc: "40+ peer-reviewed studies supporting Apollo's effectiveness" },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#00a882]/10 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-[#00a882] text-xl`}></i>
                  </div>
                  <div>
                    <h4 className="text-[#0a2a1f] font-bold text-lg mb-1">{item.title}</h4>
                    <p className="text-[#0a2a1f]/60 text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section id="programs" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-16">
            <span className="text-[#00a882] text-sm font-semibold tracking-widest uppercase">Programs</span>
            <h2 className="text-4xl md:text-5xl font-black text-[#0a2a1f] mt-4 mb-6">
              Targeted Wellness Protocols
            </h2>
            <p className="text-[#0a2a1f]/70 text-lg max-w-2xl mx-auto">
              Four scientifically-designed programs that address the most common workplace wellness challenges.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Program Tabs */}
            <div className="space-y-4">
              {programs.map((p, i) => (
                <button
                  key={p.id}
                  onClick={() => setActiveProgram(i)}
                  className={`w-full text-left p-6 rounded-2xl border-2 transition-all duration-300 cursor-pointer ${
                    activeProgram === i
                      ? "border-[#00a882] bg-[#00a882]/5"
                      : "border-[#0a2a1f]/10 hover:border-[#00a882]/30 bg-white"
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${p.color}15` }}
                    >
                      <i className={`${p.icon} text-xl`} style={{ color: p.color }}></i>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-[#0a2a1f] font-bold text-lg">{p.label}</h4>
                      <p className="text-[#0a2a1f]/60 text-sm mt-1">{p.benefit}</p>
                    </div>
                    <i className={`ri-arrow-right-line text-xl ${activeProgram === i ? "text-[#00a882]" : "text-[#0a2a1f]/30"}`}></i>
                  </div>
                </button>
              ))}
            </div>

            {/* Active Program Detail */}
            <div className="rounded-3xl bg-gradient-to-br from-[#0a2a1f] to-[#0d3d2d] p-8 text-white">
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
                style={{ backgroundColor: `${current.color}30` }}
              >
                <i className={`${current.icon} text-3xl`} style={{ color: current.color }}></i>
              </div>
              <h3 className="text-3xl font-black mb-4">{current.label}</h3>
              <p className="text-white/70 text-lg leading-relaxed mb-8">{current.desc}</p>
              <div className="flex items-center gap-3 p-4 rounded-xl bg-white/10">
                <i className="ri-flask-line text-[#00d4a8]"></i>
                <span className="text-[#00d4a8] font-semibold">{current.benefit}</span>
              </div>
              <div className="mt-8 pt-8 border-t border-white/10">
                <p className="text-white/50 text-sm mb-4">Trusted by leading organizations</p>
                <div className="flex gap-4">
                  {["Apex Corp", "NovaTech", "MedGroup", "GlobalFin"].map((company) => (
                    <span key={company} className="text-white/30 text-sm font-medium">{company}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-16">
            <span className="text-[#00a882] text-sm font-semibold tracking-widest uppercase">Process</span>
            <h2 className="text-4xl md:text-5xl font-black text-[#0a2a1f] mt-4 mb-6">
              Simple Implementation
            </h2>
            <p className="text-[#0a2a1f]/70 text-lg max-w-2xl mx-auto">
              We handle everything from assessment to ongoing optimization — you see the results.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {howItWorks.map((step, i) => (
              <div key={step.step} className="relative">
                <div className="text-7xl font-black text-[#00a882]/10 absolute -top-4 left-0">{step.step}</div>
                <div className="relative pt-12">
                  <h3 className="text-[#0a2a1f] font-bold text-xl mb-3">{step.title}</h3>
                  <p className="text-[#0a2a1f]/60 leading-relaxed">{step.desc}</p>
                </div>
                {i < howItWorks.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-px bg-[#00a882]/30"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-16">
            <span className="text-[#00a882] text-sm font-semibold tracking-widest uppercase">Testimonials</span>
            <h2 className="text-4xl md:text-5xl font-black text-[#0a2a1f] mt-4 mb-6">
              What Leaders Are Saying
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((t) => (
              <div key={t.name} className="rounded-2xl border border-[#0a2a1f]/10 bg-[#f4faf7] p-8 hover:border-[#00a882]/30 transition-all duration-300">
                <div className="flex gap-1 mb-6">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-amber-400 text-sm"></i>
                  ))}
                </div>
                <p className="text-[#0a2a1f]/80 leading-relaxed mb-6 italic">&ldquo;{t.quote}&rdquo;</p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full overflow-hidden border border-[#0a2a1f]/10">
                    <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <div className="text-[#0a2a1f] font-semibold text-sm">{t.name}</div>
                    <div className="text-[#0a2a1f]/50 text-xs">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="rounded-3xl bg-gradient-to-br from-[#0a2a1f] to-[#0d3d2d] p-8 md:p-12 text-center">
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
              Ready to Transform Your Workforce?
            </h2>
            <p className="text-white/70 text-lg mb-8 max-w-xl mx-auto">
              Get a custom quote for your organization. Programs available for teams of 25 to 25,000+.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="whitespace-nowrap px-8 py-4 rounded-full bg-[#00a882] text-white font-semibold text-base hover:bg-[#008f6e] transition-all duration-200 cursor-pointer">
                Request a Quote
              </button>
              <button className="whitespace-nowrap px-8 py-4 rounded-full border-2 border-white/30 text-white font-semibold text-base hover:bg-white/10 transition-all duration-200 cursor-pointer">
                Schedule a Demo
              </button>
            </div>
            <p className="text-white/40 text-sm mt-6">
              No commitment required. Our team will respond within 24 hours.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0a2a1f] py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#00a882] to-[#00d4a8] flex items-center justify-center">
                <i className="ri-pulse-line text-white text-sm"></i>
              </div>
              <span className="font-bold text-lg text-white">Apollo Neuro</span>
            </div>
            <div className="flex items-center gap-6">
              <a href="#" className="text-white/50 hover:text-white text-sm transition-colors">Privacy</a>
              <a href="#" className="text-white/50 hover:text-white text-sm transition-colors">Terms</a>
              <a href="#" className="text-white/50 hover:text-white text-sm transition-colors">Contact</a>
            </div>
            <p className="text-white/40 text-sm">
              © 2026 Apollo Neuro. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}